import type { SubjectLeftAction } from '@Application/Subjects/Actions/subjectLeft';
import SubjectAddress from '@Domain/Subjects/SubjectAddress';
import type { WorkflowStepRetriedAction } from '@Application/WorkflowRuns/Retry/Actions/workflowStepRetried';
import type { WorkflowRunHandledAction } from '@Application/WorkflowRuns/Handling/Actions/workflowRunHandled';
import type { WorkflowRunUnhandledAction } from '@Application/WorkflowRuns/Handling/Actions/workflowRunUnhandled';
import type { BeneficiaryWorkflowRunsFetchedAction } from '@Application/WorkflowRuns/BeneficiaryWorkflowRuns/Actions/beneficiaryWorkflowRunsFetched';
import type { WorkflowRunSourceAddedAction } from '@Application/WorkflowRuns/Sources/Actions/workflowRunSourceAdded';
import type { WorkflowRunSourceTranscriptionLaunchedAction } from '@Application/WorkflowRuns/Sources/Actions/workflowRunSourceTranscriptionLaunched';
import type { WorkflowRunAbandonedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunAbandoned';
import type { WorkflowRunChangedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunChanged';
import type { WorkflowRunFetchedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunFetched';
import type { WorkflowRunGenerationLaunchedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunGenerationLaunched';
import type { WorkflowRunRemovedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunRemoved';
import type { WorkflowRunOpenedAction } from '@Application/WorkflowRuns/WorkflowRun/Actions/workflowRunOpened';
import type { FacilityWorkflowRunsFetchedAction } from '@Application/WorkflowRuns/FacilityWorkflowRuns/Actions/facilityWorkflowRunsFetched';
import type { WorkflowRunKindsFetchedAction } from '@Application/WorkflowRuns/WorkflowRunKinds/Actions/workflowRunKindsFetched';
import type { WorkflowRunStatusFiltersFetchedAction } from '@Application/WorkflowRuns/WorkflowRunStatusFilters/Actions/workflowRunStatusFiltersFetched';
import type { UserRevokedAction } from '@Application/Security/Authentications/Actions/userRevoked';
import BeneficiaryWorkflowRunsListView from '@UI/Store/WorkflowRuns/Views/BeneficiaryWorkflowRunsListView';
import FacilityWorkflowRunView from '@UI/Store/WorkflowRuns/Views/FacilityWorkflowRunView';
import FacilityWorkflowRunsListView from '@UI/Store/WorkflowRuns/Views/FacilityWorkflowRunsListView';
import LabeledValueView from '@UI/Store/Shared/Views/LabeledValueView';
import WorkflowRunSummaryView from '@UI/Store/WorkflowRuns/Views/WorkflowRunSummaryView';
import WorkflowRunView from '@UI/Store/WorkflowRuns/Views/WorkflowRunView';
import { createSliceReducer, registerStore } from '@verclam/synapse';

export type WorkflowRunsStore = {
	facilityWorkflowRuns: FacilityWorkflowRunsListView | null;
	statusFilters: readonly LabeledValueView[] | null;
	kinds: readonly LabeledValueView[] | null;
	workflowRun: WorkflowRunView | null;
	beneficiaryWorkflowRuns: BeneficiaryWorkflowRunsListView | null;
};

const initialState: WorkflowRunsStore = {
	facilityWorkflowRuns: null,
	statusFilters: null,
	kinds: null,
	workflowRun: null,
	beneficiaryWorkflowRuns: null,
};

// The run-carrying responses of the beneficiary's run: the fetch and the open define the run on screen,
// the others answer a gesture on a run.
type WorkflowRunDefinedAction = WorkflowRunFetchedAction | WorkflowRunOpenedAction;

type WorkflowRunUpdatedAction =
	| WorkflowStepRetriedAction
	| WorkflowRunGenerationLaunchedAction
	| WorkflowRunSourceAddedAction
	| WorkflowRunSourceTranscriptionLaunchedAction;

type WorkflowRunReplacedAction = WorkflowRunDefinedAction | WorkflowRunUpdatedAction;

type Actions =
	| BeneficiaryWorkflowRunsFetchedAction
	| WorkflowRunReplacedAction
	| WorkflowRunAbandonedAction
	| WorkflowRunChangedAction
	| WorkflowRunRemovedAction
	| FacilityWorkflowRunsFetchedAction
	| WorkflowRunStatusFiltersFetchedAction
	| WorkflowRunKindsFetchedAction
	| WorkflowRunHandledAction
	| WorkflowRunUnhandledAction
	| SubjectLeftAction
	| UserRevokedAction;

const whenFacilityWorkflowRunsFetched = (
	store: WorkflowRunsStore,
	action: FacilityWorkflowRunsFetchedAction,
): WorkflowRunsStore => ({
	...store,
	facilityWorkflowRuns: new FacilityWorkflowRunsListView(
		action.payload.results.map((run) => new FacilityWorkflowRunView(run)),
		action.facilityId,
		action.queryKey,
		action.payload.totalRecords,
	),
});

const whenWorkflowRunStatusFiltersFetched = (
	store: WorkflowRunsStore,
	action: WorkflowRunStatusFiltersFetchedAction,
): WorkflowRunsStore => ({
	...store,
	statusFilters: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenWorkflowRunKindsFetched = (
	store: WorkflowRunsStore,
	action: WorkflowRunKindsFetchedAction,
): WorkflowRunsStore => ({
	...store,
	kinds: action.payload.map((entry) => new LabeledValueView(entry)),
});

const whenBeneficiaryWorkflowRunsFetched = (
	store: WorkflowRunsStore,
	action: BeneficiaryWorkflowRunsFetchedAction,
): WorkflowRunsStore => {
	return {
		...store,
		beneficiaryWorkflowRuns: new BeneficiaryWorkflowRunsListView(
			action.payload.results.map((run) => WorkflowRunSummaryView.fromPayload(run)),
			action.facilityId,
			action.typeKey,
			action.subjectId,
			action.payload.totalRecords,
		),
	};
};

const isListOf = (
	list: BeneficiaryWorkflowRunsListView | null,
	action: { facilityId: number; typeKey: string; subjectId: number },
): list is BeneficiaryWorkflowRunsListView =>
	list !== null &&
	list.facilityId === action.facilityId &&
	list.isFor(new SubjectAddress(action.typeKey, action.subjectId));

const updateListRow = (
	list: BeneficiaryWorkflowRunsListView | null,
	action: { facilityId: number; typeKey: string; subjectId: number },
	row: WorkflowRunSummaryView,
): BeneficiaryWorkflowRunsListView | null => (isListOf(list, action) ? list.updateWorkflowRun(row) : list);

const removeListRow = (
	list: BeneficiaryWorkflowRunsListView | null,
	action: { facilityId: number; typeKey: string; subjectId: number },
	runId: number,
): BeneficiaryWorkflowRunsListView | null => (isListOf(list, action) ? list.removeWorkflowRun(runId) : list);

const isDefiningAction = (action: WorkflowRunReplacedAction): action is WorkflowRunDefinedAction =>
	action.type === 'WORKFLOW_RUN_FETCHED' || action.type === 'WORKFLOW_RUN_OPENED';

/**
 * A fetch or an open defines the run on screen; a response to a gesture replaces it only when it is the
 * same run (or none is held), so a late answer never overwrites another run. The matching row of the
 * dossier list is refreshed either way; the list holds every run, so a row moves between the in-progress
 * and the completed block by itself when its `completedAt` changes.
 */
const whenWorkflowRunReplaced = (
	store: WorkflowRunsStore,
	action: WorkflowRunReplacedAction,
): WorkflowRunsStore => {
	const row = WorkflowRunSummaryView.fromRunPayload(action.payload);
	const current = store.workflowRun;
	const replaces = isDefiningAction(action) || current === null || current.id === action.payload.id;

	return {
		...store,
		workflowRun: replaces ? new WorkflowRunView(action.payload) : current,
		beneficiaryWorkflowRuns: updateListRow(store.beneficiaryWorkflowRuns, action, row),
	};
};

const whenWorkflowRunHandlingChanged = (
	store: WorkflowRunsStore,
	action: WorkflowRunHandledAction | WorkflowRunUnhandledAction,
): WorkflowRunsStore => {
	const list = store.facilityWorkflowRuns;

	if (list === null || list.facilityId !== action.facilityId) {
		return store;
	}

	return {
		...store,
		facilityWorkflowRuns: list.updateWorkflowRun(new FacilityWorkflowRunView(action.payload)),
	};
};

const whenWorkflowRunAbandoned = (
	store: WorkflowRunsStore,
	action: WorkflowRunAbandonedAction,
): WorkflowRunsStore => {
	const { runId } = action.payload;

	return {
		...store,
		workflowRun: store.workflowRun?.id === runId ? null : store.workflowRun,
		beneficiaryWorkflowRuns: removeListRow(store.beneficiaryWorkflowRuns, action, runId),
	};
};

const isListOfStream = (
	list: BeneficiaryWorkflowRunsListView | null,
	action: WorkflowRunChangedAction | WorkflowRunRemovedAction,
): list is BeneficiaryWorkflowRunsListView =>
	list?.isFor(new SubjectAddress(action.typeKey, action.subjectId)) === true;

// The dossier event stream pushes any run of its dossier: the run on screen is replaced only when it is
// that run, and the row of the same id is refreshed in the list of that dossier, never added (the list
// holds the proposals only, which a run's payload does not tell apart).
const whenWorkflowRunChanged = (
	store: WorkflowRunsStore,
	action: WorkflowRunChangedAction,
): WorkflowRunsStore => {
	const { beneficiaryWorkflowRuns } = store;

	return {
		...store,
		workflowRun:
			store.workflowRun?.id === action.payload.id
				? new WorkflowRunView(action.payload)
				: store.workflowRun,
		beneficiaryWorkflowRuns: isListOfStream(beneficiaryWorkflowRuns, action)
			? beneficiaryWorkflowRuns.updateWorkflowRun(WorkflowRunSummaryView.fromRunPayload(action.payload))
			: beneficiaryWorkflowRuns,
	};
};

const whenWorkflowRunRemoved = (
	store: WorkflowRunsStore,
	action: WorkflowRunRemovedAction,
): WorkflowRunsStore => {
	const { id } = action.payload;
	const { beneficiaryWorkflowRuns } = store;

	return {
		...store,
		workflowRun: store.workflowRun?.id === id ? null : store.workflowRun,
		beneficiaryWorkflowRuns: isListOfStream(beneficiaryWorkflowRuns, action)
			? beneficiaryWorkflowRuns.removeWorkflowRun(id)
			: beneficiaryWorkflowRuns,
	};
};

// Supervision data is volatile and refetched at every mount (no cache): nothing here is worth
// surviving a session either.
const whenSubjectLeft = (store: WorkflowRunsStore, action: SubjectLeftAction): WorkflowRunsStore => ({
	...store,
	beneficiaryWorkflowRuns: store.beneficiaryWorkflowRuns?.isFor(
		new SubjectAddress(action.payload.typeKey, action.payload.subjectId),
	)
		? null
		: store.beneficiaryWorkflowRuns,
	workflowRun: null,
});

const whenUserRevoked = (): WorkflowRunsStore => initialState;

export default registerStore<'workflowRunsStore', Actions>(
	'workflowRunsStore',
	createSliceReducer({
		FACILITY_WORKFLOW_RUNS_FETCHED: whenFacilityWorkflowRunsFetched,
		WORKFLOW_RUN_STATUS_FILTERS_FETCHED: whenWorkflowRunStatusFiltersFetched,
		WORKFLOW_RUN_KINDS_FETCHED: whenWorkflowRunKindsFetched,
		BENEFICIARY_WORKFLOW_RUNS_FETCHED: whenBeneficiaryWorkflowRunsFetched,
		WORKFLOW_RUN_FETCHED: whenWorkflowRunReplaced,
		WORKFLOW_STEP_RETRIED: whenWorkflowRunReplaced,
		WORKFLOW_RUN_OPENED: whenWorkflowRunReplaced,
		WORKFLOW_RUN_GENERATION_LAUNCHED: whenWorkflowRunReplaced,
		WORKFLOW_RUN_SOURCE_ADDED: whenWorkflowRunReplaced,
		WORKFLOW_RUN_SOURCE_TRANSCRIPTION_LAUNCHED: whenWorkflowRunReplaced,
		WORKFLOW_RUN_ABANDONED: whenWorkflowRunAbandoned,
		WORKFLOW_RUN_CHANGED: whenWorkflowRunChanged,
		WORKFLOW_RUN_REMOVED: whenWorkflowRunRemoved,
		WORKFLOW_RUN_HANDLED: whenWorkflowRunHandlingChanged,
		WORKFLOW_RUN_UNHANDLED: whenWorkflowRunHandlingChanged,
		SUBJECT_LEFT: whenSubjectLeft,
		USER_REVOKED: whenUserRevoked,
	}),
	initialState,
);
